<template>
  <main class="app-content">
    <div class="app-title">
      <div>
        <h1>
          <font-awesome-icon icon="cog"/>
          {{ $t('title') }}
        </h1>
      </div>
    </div>

    <div class="row">
      <div class="col-12">
        <div class="tile">
          <div class="form-inline">
            <b-form-input class="mr-1 mb-1" v-model="search" :placeholder="$t('searchPlaceholder')"
                          style="min-width: 280px;"></b-form-input>
            <button v-if="search" @click="search = ''" class="btn btn-secondary mr-1 mb-1">
              <font-awesome-icon icon="times"/>&nbsp;{{ $t('clear') }}
            </button>
            <div class="ml-auto text-muted small align-self-center">
              {{ $t('found', [visibleCount]) }} / {{ totalDefined }}
            </div>
          </div>
        </div>
      </div>

      <div class="col-12" v-for="group in visibleGroups" :key="group.title">
        <div class="tile">
          <h4 class="tile-title mb-3 d-flex align-items-center">
            <span><font-awesome-icon :icon="group.icon"/>&nbsp;{{ group.title }}</span>
            <span v-if="group.wesignActions && canGetSid" class="ml-auto">
              <button class="btn btn-sm btn-outline-success" @click="openGetSidModal"
                      title="Lấy SID từ MISA AMIS Platform (TTL 1 phút) → đổi sang Access Token">
                <font-awesome-icon icon="key"/>&nbsp;Lấy SID
              </button>
            </span>
          </h4>
          <!-- Readonly fields: compact inline list, 1 dòng / item -->
          <div v-if="readonlyItems(group).length" class="etc-readonly-list mb-3">
            <div class="etc-readonly-item" v-for="item in readonlyItems(group)" :key="item.key">
              <span class="etc-readonly-item__label">
                <font-awesome-icon icon="sync-alt" class="text-muted mr-1" :title="item.help || 'Tự cập nhật'"/>
                {{ item.label }}:
              </span>
              <span v-if="draft[item.key]" class="etc-readonly-item__value text-monospace">
                {{ formatReadonlyValue(item, draft[item.key]) }}
              </span>
              <span v-else class="etc-readonly-item__value text-muted font-italic">{{ $t('notSet') }}</span>
            </div>
          </div>

          <!-- Editable rows -->
          <div class="etc-row" v-for="item in editableItems(group)" :key="item.key">
            <div class="etc-row__label">
              <div class="etc-row__label-text">{{ item.label }}</div>
              <div class="etc-row__label-hint">
                <span class="badge badge-light">{{ item.key }}</span>
                <span v-if="item.help" class="ml-2" style="white-space: pre-line;">{{ item.help }}</span>
              </div>
            </div>
            <div class="etc-row__control">
              <!-- boolean -->
              <b-form-checkbox v-if="item.type === 'boolean'" switch size="lg"
                               :checked="draft[item.key] === '1'"
                               :disabled="!canEdit(item)"
                               @change="onBoolChange(item, $event)">
                <span class="small text-muted">
                  {{ draft[item.key] === '1' ? $t('on') : $t('off') }}
                </span>
              </b-form-checkbox>
              <!-- number -->
              <b-input-group v-else-if="item.type === 'number'">
                <b-form-input type="number" v-model="draft[item.key]"
                              :disabled="!canEdit(item)" :placeholder="$t('notSet')"></b-form-input>
                <b-input-group-append v-if="item.unit" is-text>{{ item.unit }}</b-input-group-append>
              </b-input-group>
              <!-- textarea -->
              <b-form-textarea v-else-if="item.type === 'textarea'" rows="3" max-rows="10"
                               v-model="draft[item.key]" :disabled="!canEdit(item)"
                               :placeholder="$t('notSet')"></b-form-textarea>
              <!-- text -->
              <b-input-group v-else>
                <b-form-input v-model="draft[item.key]" :disabled="!canEdit(item)"
                              :placeholder="$t('notSet')"></b-form-input>
                <b-input-group-append v-if="item.unit" is-text>{{ item.unit }}</b-input-group-append>
              </b-input-group>
            </div>
            <div class="etc-row__action">
              <button v-if="canEdit(item) && isDirty(item)"
                      @click="saveItem(item)" class="btn btn-sm btn-primary">
                <font-awesome-icon icon="save"/>&nbsp;{{ $t('save') }}
              </button>
              <button v-if="canEdit(item) && isDirty(item)"
                      @click="resetItem(item)" class="btn btn-sm btn-light ml-1">
                {{ $t('cancel') }}
              </button>
              <span v-else-if="!canEdit(item)" class="text-muted small">
                <font-awesome-icon icon="lock"/>
              </span>
              <span v-else-if="hasStored(item)" class="text-success small">
                <font-awesome-icon icon="check"/>
              </span>
              <span v-else class="text-muted small">{{ $t('notSet') }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Unknown keys (in DB but not defined in groups) -->
      <div class="col-12" v-if="unknownItems.length && showAdvanced">
        <div class="tile">
          <h4 class="tile-title mb-3">
            <font-awesome-icon icon="exclamation-triangle" class="text-warning"/>
            &nbsp;{{ $t('advanced') }}
            <small class="text-muted">({{ unknownItems.length }})</small>
          </h4>
          <div class="etc-row" v-for="item in unknownItems" :key="item._id">
            <div class="etc-row__label">
              <div class="etc-row__label-text">{{ item.label }}</div>
              <div class="etc-row__label-hint">
                <span class="badge badge-warning">{{ item.key }}</span>
              </div>
            </div>
            <div class="etc-row__control">
              <b-form-textarea v-model="item.value" rows="2" max-rows="8"></b-form-textarea>
            </div>
            <div class="etc-row__action">
              <button @click="saveRaw(item)" class="btn btn-sm btn-primary">
                <font-awesome-icon icon="save"/>&nbsp;{{ $t('save') }}
              </button>
              <button @click="delRaw(item._id)" class="btn btn-sm btn-danger ml-1">
                <font-awesome-icon icon="trash"/>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="col-12" v-if="unknownItems.length">
        <button @click="showAdvanced = !showAdvanced" class="btn btn-link">
          <font-awesome-icon :icon="showAdvanced ? 'chevron-up' : 'chevron-down'"/>
          &nbsp;{{ showAdvanced ? $t('hideAdvanced') : $t('showAdvanced', [unknownItems.length]) }}
        </button>
        <button v-if="canCreateRaw" @click="openCreate" class="btn btn-link">
          <font-awesome-icon icon="plus"/>&nbsp;{{ $t('create') }}
        </button>
      </div>
    </div>

    <b-modal v-model="showForm" :title="$t('create')" @ok="saveNew" no-close-on-backdrop no-enforce-focus>
      <form-input :label="$t('label')" :model="formModel" attribute="label" :errors="formErrors"></form-input>
      <form-input :label="$t('key')" :model="formModel" attribute="key" :errors="formErrors"></form-input>
      <form-textarea :label="$t('value')" :model="formModel" attribute="value" :errors="formErrors"></form-textarea>
    </b-modal>

    <get-sid-modal ref="getSidModal" @done="onGetSidDone"></get-sid-modal>
  </main>
</template>

<script>
import GetSidModal from './setting/GetSidModal.vue';

export default {
  components: { GetSidModal },
  data: function () {
    return {
      service: '/etc',
      items: [],
      draft: {},
      search: '',
      showAdvanced: false,
      showForm: false,
      formModel: {},
      formErrors: [],
      hiddenKeys: ['bot-train-fee', 'bot-chat-fee', 'bot-answer-fee', 'bot-question-fee', 'bot-teach-fee', 'zalo-server'],
      groups: [
        {
          title: 'Mobile App', icon: 'mobile-alt',
          items: [
            {key: 'mobile-ios-version', label: 'Phiên bản App iOS hiện tại', type: 'text', role: ['owner', 'admin', 'tech']},
            {key: 'mobile-android-version', label: 'Phiên bản App Android hiện tại', type: 'text', role: ['owner', 'admin', 'tech']},
          ]
        },
        {
          title: 'Phí tin nhắn & gọi', icon: 'envelope',
          items: [
            {key: 'zns-fee', label: 'Phí tin nhắn ZBS', type: 'number', unit: 'đ/tin', role: ['owner', 'admin']},
            {key: 'viber-fee', label: 'Phí tin nhắn Viber', type: 'number', unit: 'đ/tin', role: ['owner', 'admin']},
            {key: 'sms-ads-fee', label: 'Phí tin nhắn QC', type: 'number', unit: 'đ/tin', help: 'SMS quảng cáo', role: ['owner', 'admin']},
            {key: 'sms-care-fee', label: 'Phí tin nhắn CSKH', type: 'number', unit: 'đ/tin', help: 'SMS chăm sóc khách hàng', role: ['owner', 'admin']},
            {key: 'ai-fee', label: 'Phí gọi tự động', type: 'number', unit: 'đ/phút', role: ['owner', 'admin']},
          ]
        },
        {
          title: 'Gói cước & dịch vụ', icon: 'box',
          items: [
            {key: 'plan-fee', label: 'Giá gói cước thoại trọn gói', type: 'number', unit: 'đ/tháng', role: ['owner', 'admin']},
            {key: 'cdp-fee', label: 'Giá CDP', type: 'number', unit: 'đ/khách/tháng', role: ['owner', 'admin']},
            {key: 'cdp-limit', label: 'Ngưỡng khách bắt đầu tính phí CDP', type: 'number', unit: 'khách', help: 'Dưới ngưỡng này thì miễn phí', role: ['owner', 'admin']},
            {key: 'restore-fee', label: 'Giá khôi phục file ghi âm', type: 'number', unit: 'đ/file', role: ['owner', 'admin']},
            {key: 'chatbot-fee', label: 'Giá nhân viên ảo', type: 'number', unit: 'đ/tháng', role: ['owner', 'admin', 'tech']},
            {key: 'bot-speed-to-text-gpt-fee', label: 'Giá speech-to-text GPT', type: 'number', unit: 'đ/phút', role: ['owner', 'admin']},
          ]
        },
        {
          title: 'Nhân viên ảo (AI Agent)', icon: 'robot',
          items: [
            // 3 key cốt lõi business — các tham số tuning khác (vector threshold, cache TTL,
            // hard cap token, FAQ limit, policy ask loop) đã hardcode trong common helper
            // (default hợp lý, không cần admin chỉnh thường xuyên).
            {key: 'ai_agent_enabled', label: 'Bật tính năng AI Agent', type: 'boolean', help: 'TẮT = mọi bot AI Agent KHÔNG trả lời (UI khách hiện banner "Tạm dừng").\nBật trước khi cho khách kích hoạt.', role: ['owner', 'admin', 'tech']},
            {key: 'ai_agent_free_token', label: 'Token miễn phí cấp 1 lần / client', type: 'number', unit: 'token', help: 'Cấp 1 LẦN khi khách bấm "Kích hoạt" (snapshot Client.aiAgentFreeTokenInitial/Remaining).\nKHÔNG refill theo tháng. Tiêu hết → khách phải mua gói hoặc bật postpaid.\nĐổi giá trị chỉ áp dụng cho khách kích hoạt MỚI (khách đã kích hoạt giữ số đã cấp).\n\nVÍ DỤ:\n• 100.000 token ≈ 28 câu trả lời (đủ thử nghiệm)\n• 1.000.000 token ≈ 285 câu (gói thử rộng rãi)', role: ['owner', 'admin']},
            // ===== v2 (2026-06-05): 1 GIÁ FLAT DUY NHẤT + provider chọn + giá vốn để báo cáo =====
            {key: 'ai_token_price_per_1m', label: 'Giá BÁN /1M token', type: 'number', unit: 'VND/1M token', help: 'Giá KHÁCH trả cho 1 triệu token — ÁP CHO TẤT CẢ model (gpt-4o / gpt-4o-mini / deepseek-v4-flash).\n\nKHÔNG tách input/output, KHÔNG tách main/mini.\n\nÁp cho:\n• AI Agent chat (gpt-4o / deepseek)\n• Conversation QA (chấm điểm hội thoại)\n• Fast-path mini (smalltalk + faq-match)\n• Eval + Optimization (đánh giá + tối ưu bot)\n• Phần token vượt cap gói (fallback creditBalance)\n\nMẶC ĐỊNH: 200.000 VND/1M token.\n4 gói cố định tính theo giá này:\n• Basic 200k / 1M (0% discount)\n• Starter 1.9M / 10M (5%)\n• Pro 3.6M / 20M (10%)\n• Ultimate 7.65M / 45M (15%)\n\nCHỈ OWNER xem/sửa được.', role: ['owner']},
            {key: 'ai_provider', label: 'LLM Provider', type: 'string', help: 'Chọn provider LLM toàn hệ thống:\n• "openai" — gpt-4o (main) + gpt-4o-mini (mini) — chất lượng cao, giá cao\n• "deepseek" — deepseek-v4-flash — chất lượng tốt, giá rẻ ~9× OpenAI main\n\nFAIL handling: provider lỗi → trả backupAnswer cho khách + bắn alert kênh tech (Kafka slack-system-submitted group alert-tech, KHÔNG fallback tự động).\nĐổi value → cache LLM tự reload sau 30s.\n\nCHỈ OWNER xem/sửa được.', role: ['owner']},
            {key: 'ai_agent_openai_api_key', label: 'OpenAI API key', type: 'password', help: 'API key OpenAI (gpt-4o + gpt-4o-mini + embeddings fallback).\nLấy tại: https://platform.openai.com/api-keys\n\nLƯU Ý:\n• Key bắt đầu "sk-..."\n• Đổi key → cache LLM tự reload sau 30s\n• CHỈ OWNER xem được', role: ['owner']},
            {key: 'deepseek_api_key', label: 'DeepSeek API key', type: 'password', help: 'API key DeepSeek (deepseek-v4-flash). Cần khi ai_provider=deepseek.\nLấy tại: https://platform.deepseek.com/api_keys\n\nLƯU Ý: KHÔNG cần nếu chỉ dùng OpenAI. CHỈ OWNER xem được.', role: ['owner']},
            // Alert LLM fail → tự bắn qua Kafka topic 'slack-system-submitted' group 'alert-tech' (cơ chế chung), KHÔNG cần Etc webhook URL.
            // ===== Giá VỐN cho báo cáo lỗ-lãi admin (KHÔNG ảnh hưởng billing khách) =====
            {key: 'openai_main_input_cost_per_1m', label: '[Vốn] OpenAI gpt-4o input /1M', type: 'number', unit: 'VND/1M', help: 'Giá vốn input token gpt-4o (chỉ dùng báo cáo lỗ-lãi).\n\nOpenAI charge: $2.50/1M ≈ 62.000đ.\nĐổi số này KHÔNG ảnh hưởng billing khách.', role: ['owner']},
            {key: 'openai_main_output_cost_per_1m', label: '[Vốn] OpenAI gpt-4o output /1M', type: 'number', unit: 'VND/1M', help: 'Giá vốn output token gpt-4o. OpenAI charge: $10/1M ≈ 250.000đ.', role: ['owner']},
            {key: 'openai_mini_input_cost_per_1m', label: '[Vốn] OpenAI mini input /1M', type: 'number', unit: 'VND/1M', help: 'Giá vốn input gpt-4o-mini. OpenAI charge: $0.15/1M ≈ 4.000đ.', role: ['owner']},
            {key: 'openai_mini_output_cost_per_1m', label: '[Vốn] OpenAI mini output /1M', type: 'number', unit: 'VND/1M', help: 'Giá vốn output gpt-4o-mini. OpenAI charge: $0.60/1M ≈ 15.000đ.', role: ['owner']},
            {key: 'deepseek_flash_input_cost_per_1m', label: '[Vốn] DeepSeek v4-flash input /1M', type: 'number', unit: 'VND/1M', help: 'Giá vốn input deepseek-v4-flash. DeepSeek charge ≈ $0.27/1M ≈ 7.000đ.\nDeepSeek chỉ có 1 model — áp cho cả main + mini.', role: ['owner']},
            {key: 'deepseek_flash_output_cost_per_1m', label: '[Vốn] DeepSeek v4-flash output /1M', type: 'number', unit: 'VND/1M', help: 'Giá vốn output deepseek-v4-flash. DeepSeek charge ≈ $1.10/1M ≈ 28.000đ.', role: ['owner']},
            {key: 'ai_agent_custom_embed_api_key', label: 'Custom Embed API key (Gadget BGE-M3)', type: 'password', help: 'API key cho service embedding nội bộ bgem3.gadget.com.vn (Vietnamese BGE-M3).\nĐược ưu tiên cao nhất trong chuỗi fallback (custom → FPT → HF → OpenAI).\n\nCHỈ OWNER mới xem/sửa.', role: ['owner']},
            {key: 'ai_agent_fpt_cloud_api_key', label: 'FPT Cloud API key', type: 'password', help: 'API key của FPT Cloud Marketplace để dùng model "Vietnamese_Embedding" (AITeamVN).\nLấy tại: https://marketplace.fptcloud.com/vi/ai-product/AITeamVN/Vietnamese_Embedding\n\nFallback level 2 (sau Custom Embed).\nCHỈ OWNER mới xem/sửa.', role: ['owner']},
            {key: 'ai_agent_hf_api_key', label: 'HuggingFace API key', type: 'password', help: 'API key HuggingFace để dùng model BAAI/bge-m3 (đa ngôn ngữ).\nLấy tại: https://huggingface.co/settings/tokens\n\nFallback level 3 (sau FPT Cloud).\nCHỈ OWNER mới xem/sửa.', role: ['owner']},
          ]
        },
        {
          title: 'AI Trợ lý nội bộ (AI Assistant)', icon: 'headset',
          items: [
            {key: 'ai_assistant_enabled', label: 'Bật Trợ lý AI nội bộ', type: 'boolean', help: 'Kill-switch TOÀN HỆ THỐNG cho Trợ lý AI nội bộ (widget chat hỗ trợ nhân viên trong dashboard).\nTÁCH RỜI gói "nhân viên ảo" (chatbot khách) — Trợ lý nội bộ MIỄN PHÍ, Callio chịu phí token.\n\n• BẬT = mọi client thấy widget Trợ lý + dùng được (giới hạn bởi trần token/ngày bên dưới).\n• TẮT (mặc định) = ẩn widget + chặn gửi tin toàn hệ thống.\nÁp dụng sau ~30s (cache). KHÔNG còn phụ thuộc hạn gói nhân viên ảo.', role: ['owner', 'admin', 'tech']},
            {key: 'ai_assistant_daily_token_limit', label: 'Trần token / ngày / client', type: 'number', unit: 'token/ngày', help: 'Trần token AI Trợ lý nội bộ cho MỖI client trong 1 ngày (reset theo timezone client).\nChống spam đốt tiền — Callio chịu phí token nên cần trần an toàn.\nChạm trần → client bị CHẶN tới hết ngày + bắn Slack kênh tech.\n\n• 0 = KHÔNG giới hạn (rủi ro nếu bị tấn công)\n• Mặc định: 2.000.000 token/ngày/client (~250-600 câu hỏi)\nÁp dụng sau ~30s (cache).', role: ['owner', 'admin', 'tech']},
            {key: 'ai_assistant_daily_request_limit', label: 'Trần số yêu cầu / ngày / client', type: 'number', unit: 'yêu cầu/ngày', help: 'Trần SỐ yêu cầu (tin nhắn) AI Trợ lý nội bộ cho MỖI client trong 1 ngày (reset theo timezone client).\nTuyến phòng thủ chống spam theo SỐ LƯỢNG (rẻ hơn để chặn flood).\nChạm trần → client bị CHẶN tới hết ngày + bắn Slack kênh tech.\n\n• 0 = KHÔNG giới hạn\n• Mặc định: 1.000 yêu cầu/ngày/client\nÁp dụng sau ~30s (cache).', role: ['owner', 'admin', 'tech']},
          ]
        },
        {
          title: 'AI đánh giá hội thoại', icon: 'star-half-alt',
          items: [
            {key: 'conversation_eval_enabled', label: 'Bật tính năng AI đánh giá hội thoại', type: 'boolean', help: 'Kill-switch toàn hệ thống cho tính năng AI đánh giá hội thoại (call/chat).\nTẮT = ngừng submit hội thoại đi đánh giá (áp dụng sau ~30s do cache).\nBật trước khi cho client dùng.\n\nBilling: mỗi client MIỄN PHÍ 7 ngày kể từ lần đầu bật, sau đó tự tính phí theo token (gói AI nếu có, không thì trừ số dư) — KHÔNG còn cờ bật/tắt tính phí toàn hệ thống.', role: ['owner', 'admin', 'tech']},
            {key: 'eval_upsell_banner_enabled', label: 'Hiện banner chào mời bật đánh giá cuộc gọi', type: 'boolean', help: 'TẮT (mặc định) = KHÔNG hiện banner.\nBẬT = client đang gọi nhiều nhưng CHƯA bật AI đánh giá cuộc gọi sẽ thấy banner mời kích hoạt (kèm ước chi phí/tuần, tính sẵn bởi job eval-upsell-estimate). Áp dụng sau ~30s (cache).', role: ['owner', 'admin', 'tech']},
          ]
        },
        {
          title: 'Giới thiệu', icon: 'user-plus',
          items: [
            {key: 'ref-amount', label: 'Tiền cộng khi giới thiệu khách mới', type: 'number', unit: 'đ', help: 'Cộng cho người giới thiệu', role: ['owner', 'admin']},
            {key: 'ref-target', label: 'Tiền tặng khách mới từ link giới thiệu', type: 'number', unit: 'đ', role: ['owner', 'admin']},
            {key: 'ref-deposit', label: 'Số tiền nạp tối thiểu của khách được giới thiệu', type: 'number', unit: 'đ', help: 'Đạt ngưỡng mới được cộng tiền ref', role: ['owner', 'admin']},
          ]
        },
        {
          title: 'Hệ thống', icon: 'cogs',
          items: [
            {key: 'kyc-time', label: 'Thời gian duyệt hồ sơ', type: 'number', unit: 'phút', role: ['owner', 'admin', 'tech']},
            {key: 'invoice-enabled', label: 'Tự động xuất hoá đơn', type: 'boolean', role: ['owner', 'admin']},
            {key: 'client-invoice', label: 'Mở tính năng mua/xuất hoá đơn điện tử (VAT)', type: 'boolean', role: ['owner', 'admin', 'tech']},
            {key: 'ecommerce', label: 'Tính năng mua sắm', type: 'boolean', role: ['owner', 'admin', 'tech']},
            {key: 'system-alert', label: 'Bật thông báo hệ thống', type: 'boolean', role: ['owner', 'admin', 'tech']},
            {key: 'social-webhook-log-sample-rate', label: 'Tỉ lệ ghi log webhook MXH thành công',
              type: 'number', unit: '(0..1)',
              help: 'Mặc định: dev=1 (log 100%), production=0.05 (5%). Set 0 để chỉ log lỗi. Lỗi luôn ghi 100%. Đổi có hiệu lực sau ~60s, không cần restart.',
              role: ['owner', 'admin', 'tech']},
            {key: 'facebook-token-validate-enabled', label: 'Kiểm tra hạn token Facebook',
              type: 'boolean',
              help: 'BẬT = cron quét debug_token các trang Facebook để cập nhật hạn token + cảnh báo sớm cho owner/người tạo kết nối.\nTẮT = tạm dừng kiểm tra (không cập nhật hạn, không gắn nhãn hết hạn, không gửi cảnh báo).\nMặc định khi chưa set = TẮT. Đổi có hiệu lực ở lần cron chạy kế tiếp.',
              role: ['owner', 'admin', 'tech']},
            {key: 'postmark_account_token', label: 'Postmark Account API Token', type: 'password',
              help: 'Token CẤP ACCOUNT của Postmark — để tạo/xác nhận email gửi riêng của đối tác (Sender Signature) cho white-label email.\n\nKHÁC Server token đang dùng để gửi mail.\n\nLẤY TẠI: https://account.postmarkapp.com/api_tokens\n(Đăng nhập Postmark → góc phải tên Account → "API Tokens" → mục "Account API Tokens" → copy/tạo token)\n\nĐể trống → email đối tác vẫn gửi từ địa chỉ Callio (không gián đoạn). CHỈ OWNER xem/sửa được.',
              role: ['owner']},
          ]
        },
        {
          title: 'DB Health Monitor', icon: 'database',
          items: [
            {key: 'db-monitor-username', label: 'Username (clusterMonitor role)', type: 'text',
              help: 'User MongoDB có role "clusterMonitor" để worker phonenet-db-health-monitor sample serverStatus/currentOp + endpoint $indexStats chạy được. KHÔNG dùng user app chính (thiếu quyền).\n\nTạo user trên Mongo:\n  db.createUser({ user: "phonenet-monitor", pwd: "...", roles: [{ role: "clusterMonitor", db: "admin" }] })',
              role: ['owner']},
            {key: 'db-monitor-password', label: 'Password', type: 'password',
              help: 'Password của user db-monitor-username. KHÔNG share, chỉ owner xem/sửa. Đổi sẽ tự reconnect worker ở tick kế tiếp (~30s).',
              role: ['owner']},
          ]
        },
        {
          title: 'Homer / SIP Trace (Chẩn đoán cuộc gọi)', icon: 'phone',
          items: [
            {key: 'homer_url', label: 'Homer URL (siptrace)', type: 'text',
              help: 'URL hệ thống Homer siptrace. VD: https://siptrace.phonenet.io. Dùng cho tính năng "Chẩn đoán cuộc gọi".',
              role: ['owner']},
            {key: 'homer_user', label: 'Homer Username', type: 'text',
              help: 'Tài khoản đăng nhập Homer (siptrace). Chỉ owner xem/sửa.',
              role: ['owner']},
            {key: 'homer_pass', label: 'Homer Password', type: 'password',
              help: 'Mật khẩu Homer (siptrace). KHÔNG share, chỉ owner xem/sửa. Đổi có hiệu lực sau ~30s.',
              role: ['owner']},
          ]
        },
        {
          title: 'Data Lake / Athena (báo cáo >90 ngày)', icon: 'database',
          items: [
            {key: 'athena_access_key_id', label: 'Athena Access Key ID', type: 'password',
              help: 'AWS Access Key ID để truy vấn data lake (Athena/S3). Dùng cho báo cáo cuộc gọi / cước / giao dịch / chiến dịch >90 ngày (bảng *_archived).\n\nĐể trống → dùng biến môi trường ATHENA_*/AWS_* của service (fallback).\nĐổi có hiệu lực sau ~5 phút (cache) hoặc restart service. CHỈ OWNER xem/sửa được.',
              role: ['owner']},
            {key: 'athena_secret_access_key', label: 'Athena Secret Access Key', type: 'password',
              help: 'AWS Secret Access Key tương ứng. Bí mật — KHÔNG share. CHỈ OWNER xem/sửa được.\n\nLƯU Ý bảo mật: nếu key từng bị lộ (commit source...) phải xoay key mới trên AWS IAM rồi cập nhật tại đây.',
              role: ['owner']},
            {key: 'athena_region', label: 'Athena Region', type: 'text',
              help: 'Vùng AWS của Athena/S3. Mặc định: ap-southeast-1.', role: ['owner']},
            {key: 'athena_db', label: 'Athena Database', type: 'text',
              help: 'Tên database Athena (Glue catalog). Mặc định: phonenet.', role: ['owner']},
          ]
        },
        {
          title: 'Tích hợp MISA WeSign', icon: 'signature', wesignActions: true,
          items: [
            {key: 'esign-admin-enabled', label: 'Bật đẩy hợp đồng KYC lên MISA WeSign', type: 'boolean',
              help: 'Tắt = ẩn nút "Đẩy MISA" trong KYC detail. Doc đang chờ ký vẫn xử lý callback', role: ['owner']},
            {key: 'esign-admin-provider-name', label: 'Tên hiển thị provider', type: 'text',
              help: 'VD: MISA WeSign — Callio', role: ['owner']},
            {key: 'esign-admin-client-id', label: 'MISA Client ID', type: 'text',
              help: 'Cấp bởi MISA partner', role: ['owner']},
            {key: 'esign-admin-client-secret', label: 'MISA Client Secret', type: 'text',
              help: 'Bí mật — không share', role: ['owner']},
            {key: 'esign-admin-app-code', label: 'MISA App Code', type: 'text',
              help: 'Định danh app trong doc payload', role: ['owner']},
            {key: 'esign-admin-access-token', label: 'Access Token (chỉ xem)', type: 'textarea', readonly: true,
              help: 'Worker tự refresh — không sửa tay', role: ['owner', 'account']},
            {key: 'esign-admin-amis-sid', label: 'AMIS sid (chỉ xem)', type: 'textarea', readonly: true,
              help: 'Auto lấy từ button "Lấy SID" — không paste tay', role: ['owner', 'account']},
            {key: 'esign-admin-refresh-token', label: 'Refresh Token (chỉ xem)', type: 'textarea', readonly: true,
              help: 'Worker tự cập nhật', role: ['owner', 'account']},
            {key: 'esign-admin-token-expire-time', label: 'Hạn token (timestamp ms — chỉ xem)', type: 'number', readonly: true,
              help: 'Worker tự cập nhật', role: ['owner', 'account']},
          ]
        },
      ]
    }
  },
  i18n: {
    messages: {
      vi: {
        title: 'Tham số hệ thống',
        searchPlaceholder: 'Tìm theo tên hoặc mã...',
        clear: 'Xoá',
        label: 'Tên tham số',
        key: 'Mã',
        value: 'Giá trị',
        found: 'Đang hiện {0}',
        notSet: 'Chưa đặt',
        on: 'Bật',
        off: 'Tắt',
        save: 'Lưu',
        cancel: 'Huỷ',
        create: 'Thêm tham số mới',
        advanced: 'Tham số nâng cao',
        showAdvanced: 'Hiện tham số nâng cao ({0})',
        hideAdvanced: 'Ẩn tham số nâng cao',
      },
      en: {
        title: 'System parameters',
        searchPlaceholder: 'Search by name or key...',
        clear: 'Clear',
        label: 'Parameter',
        key: 'Key',
        value: 'Value',
        found: 'Showing {0}',
        notSet: 'Not set',
        on: 'On',
        off: 'Off',
        save: 'Save',
        cancel: 'Cancel',
        create: 'Create new parameter',
        advanced: 'Advanced parameters',
        showAdvanced: 'Show advanced ({0})',
        hideAdvanced: 'Hide advanced',
      }
    }
  },
  computed: {
    totalDefined() {
      const role = this.$store.state.user.role;
      return this.groups
        .filter(g => role !== 'account' || g.wesignActions)
        .reduce((s, g) => s + g.items.filter(i => !i.role || i.role.includes(role)).length, 0);
    },
    visibleGroups() {
      const role = this.$store.state.user.role;
      const kw = (this.search || '').toLowerCase().trim();
      return this.groups
        // account chỉ được vào Tham số để lấy SID MISA — không thấy nhóm cấu hình khác
        .filter(g => role !== 'account' || g.wesignActions)
        .map(g => ({
          ...g,
          items: g.items.filter(item => {
            if (item.role && !item.role.includes(role)) return false;
            if (!kw) return true;
            return item.label.toLowerCase().includes(kw) || item.key.toLowerCase().includes(kw);
          })
        }))
        .filter(g => g.items.length);
    },
    visibleCount() {
      return this.visibleGroups.reduce((s, g) => s + g.items.length, 0);
    },
    knownKeySet() {
      const set = new Set();
      this.groups.forEach(g => g.items.forEach(i => set.add(i.key)));
      return set;
    },
    unknownItems() {
      if (this.$store.state.user.role === 'account') return [];
      return this.items.filter(i => !this.knownKeySet.has(i.key) && !this.hiddenKeys.includes(i.key));
    },
    canCreateRaw() {
      return ['owner', 'admin', 'tech'].includes(this.$store.state.user.role);
    },
    canGetSid() {
      return ['owner', 'account'].includes(this.$store.state.user.role);
    }
  },
  methods: {
    load() {
      let loader = this.$loading.show();
      this.$service.get(this.service, {params: {page: 1, pageSize: 500}}).then(response => {
        this.items = response.data.docs || [];
        this.rebuildDraft();
      }).catch(error => {
        if (error.response && error.response.data && error.response.data.message) {
          this.$swal({text: error.response.data.message, icon: 'error'});
        }
      }).finally(() => loader.hide());
    },
    rebuildDraft() {
      const draft = {};
      this.items.forEach(it => {
        draft[it.key] = it.value == null ? '' : String(it.value);
      });
      this.groups.forEach(g => g.items.forEach(item => {
        if (draft[item.key] == null) {
          draft[item.key] = item.type === 'boolean' ? '0' : '';
        }
      }));
      this.draft = draft;
    },
    findStored(key) {
      return this.items.find(i => i.key === key);
    },
    hasStored(item) {
      return !!this.findStored(item.key);
    },
    originalValue(item) {
      const stored = this.findStored(item.key);
      if (stored) return String(stored.value);
      return item.type === 'boolean' ? '0' : '';
    },
    isDirty(item) {
      const cur = this.draft[item.key] == null ? '' : String(this.draft[item.key]);
      return cur !== this.originalValue(item);
    },
    canEdit(item) {
      if (item.readonly) return false;
      if (!item.role) return true;
      return item.role.includes(this.$store.state.user.role);
    },
    readonlyItems(group) {
      return (group.items || []).filter(i => i.readonly);
    },
    editableItems(group) {
      return (group.items || []).filter(i => !i.readonly);
    },
    formatReadonlyValue(item, value) {
      if (!value) return '';
      const v = String(value);
      // Timestamp ms → date readable
      if (item.key === 'esign-admin-token-expire-time') {
        const n = Number(v);
        if (!n || n <= 0) return v;
        const d = new Date(n);
        const left = n - Date.now();
        const leftStr = left > 0
          ? ` (còn ${Math.round(left / 60000)} phút)`
          : ' (đã hết hạn)';
        return d.toLocaleString('vi-VN') + leftStr;
      }
      // Token / secret → mask middle
      if (v.length > 32) {
        return v.substring(0, 12) + '…' + v.substring(v.length - 6) + `  (${v.length} ký tự)`;
      }
      return v;
    },
    onBoolChange(item, checked) {
      this.$set(this.draft, item.key, checked ? '1' : '0');
      if (this.isDirty(item)) this.saveItem(item);
    },
    resetItem(item) {
      this.$set(this.draft, item.key, this.originalValue(item));
    },
    saveItem(item) {
      const value = this.draft[item.key];
      if (value === '' || value == null) {
        this.$swal({text: this.$t('notSet'), icon: 'warning'});
        return;
      }
      const payload = {key: item.key, label: item.label, value: String(value)};
      const stored = this.findStored(item.key);
      let loader = this.$loading.show();
      const req = stored
        ? this.$service.put(this.service + '/' + stored._id, payload)
        : this.$service.post(this.service, payload);
      req.then(response => {
        if (response.data) {
          this.$swal({title: this.$t('updated'), icon: 'success', toast: true, position: 'top-end', timer: 1500, showConfirmButton: false});
          this.load();
        } else {
          this.$swal({title: this.$t('error'), text: this.$t('updateFailed'), icon: 'error'});
        }
      }).catch(error => {
        const msg = error.response && error.response.data && error.response.data.message;
        this.$swal({text: msg || this.$t('updateFailed'), icon: 'error'});
      }).finally(() => loader.hide());
    },
    saveRaw(item) {
      const payload = {key: item.key, label: item.label, value: String(item.value || '')};
      let loader = this.$loading.show();
      this.$service.put(this.service + '/' + item._id, payload).then(response => {
        if (response.data) {
          this.$swal({title: this.$t('updated'), icon: 'success', toast: true, position: 'top-end', timer: 1500, showConfirmButton: false});
          this.load();
        }
      }).catch(error => {
        const msg = error.response && error.response.data && error.response.data.message;
        this.$swal({text: msg || this.$t('updateFailed'), icon: 'error'});
      }).finally(() => loader.hide());
    },
    delRaw(id) {
      this.$swal({
        title: this.$t('deleteConfirm'),
        text: this.$t('deleteAlert'),
        icon: 'warning',
        showCancelButton: true
      }).then(result => {
        if (!result.value) return;
        let loader = this.$loading.show();
        this.$service.delete(this.service + '/' + id).then(response => {
          if (response.data) {
            this.$swal({title: this.$t('deleted'), icon: 'success', toast: true, position: 'top-end', timer: 1500, showConfirmButton: false});
            this.load();
          }
        }).finally(() => loader.hide());
      });
    },
    openCreate() {
      this.formModel = {key: '', label: '', value: ''};
      this.formErrors = [];
      this.showForm = true;
    },
    saveNew(evt) {
      if (evt) evt.preventDefault();
      let loader = this.$loading.show();
      this.$service.post(this.service, this.formModel).then(response => {
        if (response.data) {
          this.showForm = false;
          this.$swal({title: this.$t('created'), icon: 'success', toast: true, position: 'top-end', timer: 1500, showConfirmButton: false});
          this.load();
        }
      }).catch(error => {
        if (error.response && error.response.data && error.response.data.message) {
          this.$swal({text: error.response.data.message, icon: 'error'});
        } else {
          this.formErrors = error.response.data;
        }
      }).finally(() => loader.hide());
    },
    // ===== MISA WeSign actions =====
    openGetSidModal() {
      // Flow MISA chốt: admin paste URL Google → BE parse sid + đổi ngay sang Access Token
      // (sid TTL chỉ 1 phút theo docs MISA mục API lấy Token)
      this.$refs.getSidModal.open();
    },
    onGetSidDone(success) {
      if (success) {
        this.load();  // reload Etc → access token + refresh token hiện trong readonly fields
      }
    },
  },
  mounted() {
    this.load();
  }
}
</script>

<style scoped>
.tile-title {
  border-bottom: 1px solid #eee;
  padding-bottom: 8px;
  font-size: 16px;
  font-weight: 600;
}

.etc-row {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 12px 0;
  border-bottom: 1px solid #f3f3f3;
}

.etc-row:last-child {
  border-bottom: none;
}

.etc-row__label {
  flex: 0 0 38%;
  min-width: 0;
}

.etc-row__label-text {
  font-weight: 500;
  color: #333;
}

.etc-row__label-hint {
  margin-top: 4px;
  font-size: 12px;
  color: #999;
}

.etc-row__control {
  flex: 1 1 auto;
  min-width: 0;
}

.etc-row__preview {
  margin-top: 4px;
  font-size: 12px;
  color: #28a745;
}

.etc-row__action {
  flex: 0 0 auto;
  align-self: center;
  min-width: 90px;
  text-align: right;
}

/* Compact list for readonly fields — 1 dòng/row, không grid 3 cột */
.etc-readonly-list {
  background: #f8f9fa;
  border-radius: 6px;
  padding: 8px 12px;
  border: 1px solid #eee;
}
.etc-readonly-item {
  display: flex;
  align-items: baseline;
  gap: 8px;
  padding: 3px 0;
  font-size: 12.5px;
  line-height: 1.4;
}
.etc-readonly-item + .etc-readonly-item {
  border-top: 1px dashed #e9ecef;
}
.etc-readonly-item__label {
  flex: 0 0 auto;
  color: #6c757d;
  min-width: 180px;
}
.etc-readonly-item__value {
  flex: 1 1 auto;
  word-break: break-all;
  color: #212529;
}
</style>
